// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React from "react";
import OperatingHoursEditor from "./OperatingHoursEditor";
import {
  slugify,
  TIMEZONE_OPTIONS,
  DEFAULT_TIMEZONE,
  COUNTRY_OPTIONS,
  DEFAULT_COUNTRY,
} from "../../utils/locationFormHelpers";

const LocationFormFields = ({
  formData,
  setFormData,
  slugDirty,
  setSlugDirty,
  slotBeforeFlags = null,
}) => (
  <>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="lg:col-span-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
          Name *
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => {
            const name = e.target.value;
            setFormData((p) => ({
              ...p,
              name,
              // Auto-fill slug until the admin manually edits it.
              slug: slugDirty ? p.slug : slugify(name),
            }));
          }}
          className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
          VHP-Code
        </label>
        <input
          type="text"
          value={formData.vhp_code}
          onChange={(e) =>
            setFormData((p) => ({ ...p, vhp_code: e.target.value }))
          }
          className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
          Slug
        </label>
        <input
          type="text"
          value={formData.slug}
          onChange={(e) => {
            setSlugDirty(true);
            setFormData((p) => ({ ...p, slug: e.target.value }));
          }}
          placeholder="wird aus dem Namen erzeugt"
          className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
        Beschreibung
      </label>
      <textarea
        value={formData.description}
        onChange={(e) =>
          setFormData((p) => ({ ...p, description: e.target.value }))
        }
        rows={3}
        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
        Beschreibung (HTML)
      </label>
      <textarea
        value={formData.description_html}
        onChange={(e) =>
          setFormData((p) => ({ ...p, description_html: e.target.value }))
        }
        rows={3}
        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-xs"
      />
    </div>

    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
          Breitengrad
        </label>
        <input
          type="number"
          step="any"
          value={formData.latitude}
          onChange={(e) =>
            setFormData((p) => ({ ...p, latitude: e.target.value }))
          }
          placeholder="z. B. 52.5127"
          className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
          Längengrad
        </label>
        <input
          type="number"
          step="any"
          value={formData.longitude}
          onChange={(e) =>
            setFormData((p) => ({ ...p, longitude: e.target.value }))
          }
          placeholder="z. B. 13.3219"
          className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
    </div>

    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
          Zeitzone
        </label>
        <select
          value={formData.timezone || DEFAULT_TIMEZONE}
          onChange={(e) =>
            setFormData((p) => ({ ...p, timezone: e.target.value }))
          }
          className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {/* Preserve any legacy/non-standard timezone already on the record
              so editing doesn't silently overwrite it. */}
          {formData.timezone &&
            !TIMEZONE_OPTIONS.includes(formData.timezone) && (
              <option value={formData.timezone}>{formData.timezone}</option>
            )}
          {TIMEZONE_OPTIONS.map((tz) => (
            <option key={tz} value={tz}>
              {tz}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
          Land (ISO-3)
        </label>
        <select
          value={formData.country || DEFAULT_COUNTRY}
          onChange={(e) =>
            setFormData((p) => ({ ...p, country: e.target.value }))
          }
          className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {formData.country &&
            !COUNTRY_OPTIONS.some((c) => c.code === formData.country) && (
              <option value={formData.country}>{formData.country}</option>
            )}
          {COUNTRY_OPTIONS.map((c) => (
            <option key={c.code} value={c.code}>
              {c.label} ({c.code})
            </option>
          ))}
        </select>
      </div>
    </div>

    <div className="grid grid-cols-3 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
          Straße
        </label>
        <input
          type="text"
          value={formData.address_street}
          onChange={(e) =>
            setFormData((p) => ({ ...p, address_street: e.target.value }))
          }
          className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
          Stadt
        </label>
        <input
          type="text"
          value={formData.address_city}
          onChange={(e) =>
            setFormData((p) => ({ ...p, address_city: e.target.value }))
          }
          className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
          PLZ
        </label>
        <input
          type="text"
          value={formData.address_postal_code}
          onChange={(e) =>
            setFormData((p) => ({
              ...p,
              address_postal_code: e.target.value,
            }))
          }
          className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
        Öffnungszeiten
      </label>
      <OperatingHoursEditor
        value={formData.operating_hours}
        onChange={(v) => setFormData((p) => ({ ...p, operating_hours: v }))}
      />
    </div>

    {slotBeforeFlags}

    <div className="grid grid-cols-2 gap-4">
      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300">
        <input
          type="checkbox"
          checked={!!formData.is_active}
          onChange={(e) =>
            setFormData((p) => ({ ...p, is_active: e.target.checked }))
          }
          className="h-4 w-4 rounded border-gray-300"
        />
        Aktiv
      </label>
      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300">
        <input
          type="checkbox"
          checked={!!formData.is_public}
          onChange={(e) =>
            setFormData((p) => ({ ...p, is_public: e.target.checked }))
          }
          className="h-4 w-4 rounded border-gray-300"
        />
        Öffentlich
      </label>
    </div>
  </>
);

export default LocationFormFields;
